<template>
  <div class="col-md-8 mx-auto">
    <div v-if="loading" class="text-center py-5">
      <b-spinner label="Loading..." variant="primary" class="mr-2"></b-spinner>
      <span class="font-weight-bold">Loading staff leave requests...</span>
    </div>

    <div v-else>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="d-flex" style="gap: 8px;">
          <b-form-select v-model="selectedLeaveCycleId" @change="fetchLeaves" :options="leaveCycleOptions" class="w-auto"></b-form-select>
          <b-form-select v-model="selectedStatus" @change="fetchLeaves" :options="statusOptions" class="w-auto"></b-form-select>
        </div>

        <div style="width: 40%;">
          <b-form-input v-model="searchQuery" placeholder="Search by name or email..."
            @input="debouncedSearch"></b-form-input>
        </div>
      </div>

      <table class="table table-striped table-bordered">
        <thead class="thead-dark text-left">
          <tr>
            <th>Sl. No.</th>
            <th>Staff</th>
            <th>Leave Type</th>
            <th>Session</th>
            <th>From</th>
            <th>To</th>
            <th>Days</th>
            <th>Reason</th>
            <th v-if="selectedStatus === 'rejected'">Reject Reason</th>
            <th>Attachment</th>
            <th>Status</th>
            <th v-if="selectedStatus === 'pending'">Action</th>
            <th v-if="selectedStatus === 'pending'" class="text-center">WhatsApp Notification</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="leaveRequests.length === 0">
            <td colspan="12" class="text-center text-muted py-4">No leave requests found.</td>
          </tr>
          <tr v-for="(leave, index) in leaveRequests" :key="leave.id">
            <td>{{ (currentPage - 1) * 15 + (index + 1) }}</td>
            <td>
              {{ leave.faculty ? leave.faculty.first_name + ' ' + leave.faculty.last_name : '' }}
              <br>
              <small class="text-muted">{{ leave.faculty ? leave.faculty.email : '' }}</small>
            </td>
            <td>{{ leave.leave_type ? leave.leave_type.name : '' }}</td>
            <td>{{ formatSession(leave.session) }}</td>
            <td>{{ formatDate(leave.from_date) }}</td>
            <td>{{ formatDate(leave.to_date) }}</td>
            <td>{{ formatDays(leave.days_count) }}</td>
            <td>{{ leave.reason }}</td>
            <td v-if="selectedStatus === 'rejected'">{{ leave.reject_reason }}</td>
            <td class="text-center">
              <button v-if="leave.files && leave.files.length > 0"
                class="btn btn-sm btn-outline-secondary"
                @click="previewAttachment(leave.files[0])">
                <i class="fas fa-paperclip" style="font-size: 12px;"></i>
              </button>
            </td>
            <td>
              <span v-if="leave.status === 'approved'" class="text-success font-weight-bold">Approved</span>
              <span v-else-if="leave.status === 'rejected'" class="text-danger font-weight-bold">Rejected</span>
              <span v-else-if="hod && leave.recommendation_status === 'recommended'" class="text-success font-weight-bold">Recommended</span>
              <span v-else class="text-warning font-weight-bold">Pending</span>
              <small v-if="leave.recommendation_status === 'recommended' && leave.recommender && (hod || leave.status !== 'pending')" class="d-block text-muted">
                Recommended by {{ leave.recommender.first_name }} {{ leave.recommender.last_name }}
              </small>
              <small v-else-if="leave.recommendation_status === 'not_recommended' && leave.recommender" class="d-block text-muted">
                Rejected by {{ leave.recommender.first_name }} {{ leave.recommender.last_name }}
              </small>
            </td>
            <td v-if="selectedStatus === 'pending'" class="text-nowrap">
              <template v-if="hod">
                <button class="btn btn-success btn-sm mr-1" @click="updateStatus(leave.id, 'recommended')">Recommend</button>
                <button class="btn btn-danger btn-sm" @click="openRejectModal(leave.id)">Reject</button>
              </template>
              <template v-else>
                <button class="btn btn-success btn-sm mr-1" @click="updateStatus(leave.id, 'approved')">Approve</button>
                <button class="btn btn-danger btn-sm" @click="openRejectModal(leave.id)">Reject</button>
              </template>
            </td>
            <td v-if="selectedStatus === 'pending'" class="text-center">
              <b-form-checkbox :checked="!whatsAppOff[leave.id]" @change="$set(whatsAppOff, leave.id, !$event)"></b-form-checkbox>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="d-flex justify-content-center mt-3" v-if="lastPage > 1">
        <b-pagination v-model="currentPage" :total-rows="lastPage * 15" :per-page="15"
          @change="fetchLeaves"></b-pagination>
      </div>
    </div>

    <b-modal :id="rejectModalId" title="Rejection Reason" @ok="submitRejection">
      <b-form-group label="Reason">
        <b-form-input v-model="rejectReason" placeholder="Enter reason (optional)"></b-form-input>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    // HOD view: recommends the requests of their department staff before they reach the approver
    hod: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: false,
      cloudStorageRoot: "",
      leaveRequests: [],
      selectedStatus: "pending",
      selectedLeaveCycleId: null,
      leaveCycles: [],
      searchQuery: "",
      currentPage: 1,
      lastPage: 1,
      rejectReason: "",
      selectedLeaveId: null,
      whatsAppOff: {}, // leave ids whose WhatsApp notification was unticked (ticked by default)
      searchTimer: null,
    };
  },

  computed: {
    statusOptions() {
      return [
        { value: "pending", text: "Pending" },
        this.hod
          ? { value: "recommended", text: "Recommended" }
          : { value: "approved", text: "Approved" },
        { value: "rejected", text: "Rejected" },
      ];
    },

    apiPath() {
      return this.hod ? "faculty-leave/hod-requests" : "faculty-leave/requests";
    },

    rejectModalId() {
      return this.hod ? "facultyHodRejectModal" : "facultyRejectModal";
    },

    leaveCycleOptions() {
      return [
        ...this.leaveCycles.map(c => ({ value: c.id, text: c.name })),
      ];
    },
  },

  async created() {
    this.cloudStorageRoot = this.$store.getters.getAPIKey.userAttachments;
    await this.fetchLeaveCycles();
    await this.fetchLeaves();
  },

  methods: {
    async fetchLeaveCycles() {
      try {
        const response = await axios.get(`${this.$store.getters.getAPIKey.mainAPI}/faculty-leave`);
        this.leaveCycles = response.data.data.leave_cycles ?? [];
        if (this.leaveCycles.length) this.selectedLeaveCycleId = response.data.data.currentLeaveCycleId ?? null;
      } catch {
        this.$toast.error("Failed to load leave cycles", { position: "top", duration: 3000 });
      }
    },

    debouncedSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchLeaves(), 400);
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-GB");
    },

    formatSession(session) {
      const map = { forenoon: 'Forenoon', afternoon: 'Afternoon', full_day: 'Full Day' };
      return map[session] ?? session ?? '-';
    },

    formatDays(count) {
      if (count == null || count === '') return '-';
      if (parseFloat(count) === 0.5) return 'Half Day';
      const rounded = Math.round(count);
      return `${rounded} ${rounded === 1 ? 'Day' : 'Days'}`;
    },

    previewAttachment(file) {
      window.open(this.cloudStorageRoot + file.file_path + file.file_name, "_blank");
    },

    async fetchLeaves() {
      this.loading = true;
      try {
        let url = `${this.$store.getters.getAPIKey.mainAPI}/${this.apiPath}?status=${this.selectedStatus}&search=${this.searchQuery}`;
        if (this.selectedLeaveCycleId) url += `&leave_cycle_id=${this.selectedLeaveCycleId}`;
        const response = await axios.get(url);
        const data = response.data.data ?? response.data;
        this.leaveRequests = Array.isArray(data) ? data : data.data ?? [];
      } catch {
        this.$toast.error("Failed to load staff leave requests", { position: "top", duration: 3000 });
      } finally {
        this.loading = false;
      }
    },

    openRejectModal(leaveId) {
      this.selectedLeaveId = leaveId;
      this.rejectReason = "";
      this.$bvModal.show(this.rejectModalId);
    },

    async submitRejection() {
      await this.updateStatus(this.selectedLeaveId, "rejected", this.rejectReason);
    },

    async updateStatus(leaveId, action, reason = null) {
      try {
        const response = await axios.post(
          `${this.$store.getters.getAPIKey.mainAPI}/${this.apiPath}/${leaveId}/action`,
          { action, reject_reason: reason, send_whatsapp: !this.whatsAppOff[leaveId] }
        );
        const message = response.data?.message ?? "Leave request updated";
        this.$toast.success(message, { position: "top", duration: 3000 });
        this.leaveRequests = this.leaveRequests.filter(l => l.id !== leaveId);
      } catch (error) {
        const message = error.response?.data?.message ?? "Failed to update leave request";
        this.$toast.error(message, { position: "top", duration: 3000 });
        // another HOD of the department already acted, the row is no longer pending
        if (this.hod && [404, 409].includes(error.response?.status)) {
          this.leaveRequests = this.leaveRequests.filter(l => l.id !== leaveId);
        }
      }
    },
  },
};
</script>
